import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import Modal from '@components/modal/Modal'

import { addMonths, addYears, format, isSameMonth, startOfYear, subYears } from 'date-fns'

import useI18n from '@i18n/useI18n'

interface Props {
  month: Date
  onMonth: (month: Date) => void
}

const MonthSelectorComponent = ({ month, onMonth }: Props) => {
  const i18n = useI18n()

  const [year, setYear] = React.useState(startOfYear(month))

  const MONTHS = React.useMemo(() => Array.from(new Array(12)).map((_, idx) => addMonths(year, idx)), [year])

  const onItemSelected = (item: Date) => {
    onMonth(item)
    MonthSelector.close()
  }

  const renderItem = ({ item }: { item: Date }) => {
    return (
      <Item activeOpacity={0.8} onPress={() => onItemSelected(item)} active={isSameMonth(month, item)}>
        <ItemText>{format(item, 'MMMM', { locale: i18n.locales[i18n.lang] })}</ItemText>
      </Item>
    )
  }

  return (
    <MainContainer>
      <Header>
        <YearSelector activeOpacity={0.8} onPress={() => setYear(subYears(year, 1))}>
          <Icon name="arrow-left" size={30} />
        </YearSelector>
        <Title>{format(year, 'yyyy')}</Title>
        <YearSelector activeOpacity={0.8} onPress={() => setYear(addYears(year, 1))} right>
          <Icon name="arrow-left" size={30} />
        </YearSelector>
      </Header>

      <FlatList data={MONTHS} renderItem={renderItem} keyExtractor={(item) => item.toISOString()} numColumns={3} />
    </MainContainer>
  )
}

const MainContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  padding: 16px;
`

const Header = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 15px;
  margin-bottom: 15px;
`

const YearSelector = styled(TouchableOpacity)<{ right?: boolean }>`
  ${(props) => props.right && 'transform: rotate(180deg)'}
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Item = styled(TouchableOpacity)<{ active: boolean }>`
  height: 100px;
  width: 100px;
  justify-content: center;
  align-items: center;
  border: ${(props) => props.theme.colors.secondaryActive} solid ${(props) => (props.active ? 2 : 0)}px;
`

const ItemText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-transform: capitalize;
`

const MonthSelector = {
  open: (config: Props) => Modal.open({ Content: () => <MonthSelectorComponent {...config} />, cancel: true }),
  close: () => Modal.close(),
}

export default MonthSelector
